/**
 * ReferralPage - User's referral dashboard
 * Shows referral code, stats, and sharing options
 */

import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Gift,
  Users,
  DollarSign,
  Copy,
  Share2,
  ArrowLeft,
  Loader2,
  CheckCircle,
  Clock,
  Sparkles,
  Twitter,
  Mail,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stats card component
const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="p-4 bg-zinc-800/50 rounded-xl border border-zinc-800">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-xs text-zinc-500">{label}</p>
        <p className="text-xl font-bold text-white">{value}</p>
      </div>
    </div>
  </div>
);

const ReferralPage = () => {
  const { user, loading: authLoading } = useAuth();
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const fetchReferralData = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/store/referral/${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setReferralData(data);
      }
    } catch (error) {
      console.error('Error fetching referral data:', error);
      toast.error('Failed to load referral info');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchReferralData();
  }, [fetchReferralData]);

  const handleCopyCode = async () => {
    if (!referralData?.referral_code) return;

    try {
      await navigator.clipboard.writeText(referralData.referral_code);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy code');
    }
  };

  const handleCopyLink = async () => {
    if (!referralData?.referral_code) return;

    const link = `${window.location.origin}/signup?ref=${referralData.referral_code}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Referral link copied!');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleShareTwitter = () => {
    const link = `${window.location.origin}/signup?ref=${referralData?.referral_code}`;
    const text = `Join me on String Sauce and we'll both get $5 store credit! Use my referral code: ${referralData?.referral_code}`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(link)}`, '_blank');
  };

  const handleShareEmail = () => {
    const link = `${window.location.origin}/signup?ref=${referralData?.referral_code}`;
    const subject = "Join me on String Sauce - We'll both get $5!";
    const body = `Hey!\n\nI've been using String Sauce for music production and thought you'd love it too.\n\nSign up using my referral link and we'll both get $5 store credit:\n${link}\n\nOr use my code: ${referralData?.referral_code}\n\nSee you there!`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };

  // Redirect if not logged in
  if (!authLoading && !user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/store" className="p-2 hover:bg-zinc-800 rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold">Refer a Friend</h1>
                <p className="text-sm text-zinc-500">Share and earn $5 for each friend who joins</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-10 h-10 text-violet-400 animate-spin" />
          </div>
        ) : (
          <>
            {/* Hero section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-8"
            >
              <div className="inline-flex p-4 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-full mb-4">
                <Gift className="w-12 h-12 text-amber-400" />
              </div>
              <h2 className="text-3xl font-bold mb-2">Give $5, Get $5</h2>
              <p className="text-zinc-400 max-w-md mx-auto">
                Share your referral code with friends. When they make their first purchase, 
                you'll both receive $5 in store credit!
              </p>
            </motion.div>

            {/* Referral code card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-violet-500/20 to-purple-500/20 border border-violet-500/30 rounded-2xl p-6 mb-8"
            >
              <p className="text-sm text-zinc-400 text-center mb-2">Your Referral Code</p>
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="text-4xl font-mono font-bold text-white tracking-wider">
                  {referralData?.referral_code || '---'}
                </span>
                <button
                  onClick={handleCopyCode}
                  className="p-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
                  title="Copy code"
                >
                  {copied ? (
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                  ) : (
                    <Copy className="w-5 h-5 text-zinc-400" />
                  )}
                </button>
              </div>

              {/* Share buttons */}
              <div className="flex flex-wrap justify-center gap-3">
                <Button
                  onClick={handleCopyLink}
                  variant="outline"
                  className="border-zinc-700"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
                <Button
                  onClick={handleShareTwitter}
                  variant="outline"
                  className="border-zinc-700"
                >
                  <Twitter className="w-4 h-4 mr-2" />
                  Tweet
                </Button>
                <Button
                  onClick={handleShareEmail}
                  variant="outline"
                  className="border-zinc-700"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </Button>
              </div>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
            >
              <StatCard
                icon={Users}
                label="Successful Referrals"
                value={referralData?.stats?.successful_referrals || 0}
                color="bg-emerald-500/20 text-emerald-400"
              />
              <StatCard
                icon={Clock}
                label="Pending"
                value={referralData?.stats?.pending_referrals || 0}
                color="bg-amber-500/20 text-amber-400"
              />
              <StatCard
                icon={DollarSign}
                label="Total Earned"
                value={`$${(referralData?.stats?.total_earned || 0).toFixed(2)}`}
                color="bg-violet-500/20 text-violet-400"
              />
              <StatCard
                icon={Sparkles}
                label="Available Credit"
                value={`$${(referralData?.available_credits || 0).toFixed(2)}`}
                color="bg-blue-500/20 text-blue-400"
              />
            </motion.div>

            {/* How it works */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-zinc-800/50 rounded-2xl p-6 border border-zinc-800"
            >
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Share2 className="w-5 h-5 text-violet-400" />
                How It Works
              </h3>
              
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-violet-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-violet-400 font-bold">1</span>
                  </div>
                  <div>
                    <p className="font-medium text-white">Share your code</p>
                    <p className="text-sm text-zinc-500">Send your unique referral code to friends</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-violet-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-violet-400 font-bold">2</span>
                  </div>
                  <div>
                    <p className="font-medium text-white">They sign up & purchase</p>
                    <p className="text-sm text-zinc-500">Your friend creates an account and makes their first purchase</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-emerald-400 font-bold">3</span>
                  </div>
                  <div>
                    <p className="font-medium text-white">You both get $5!</p>
                    <p className="text-sm text-zinc-500">Both you and your friend receive $5 store credit</p>
                  </div>
                </div>
              </div>
              
              <p className="text-xs text-zinc-600 mt-4">
                Credits expire after 1 year. There's no limit to how many friends you can refer!
              </p>
            </motion.div>

            {/* CTA */}
            <div className="mt-8 text-center">
              <Link to="/store">
                <Button className="bg-violet-500 hover:bg-violet-600">
                  Browse Store
                </Button>
              </Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default ReferralPage;
